import { useCallback, useEffect, useState } from 'react';

import { getCollectionMe } from '../services/users';
import { normalizeId } from '../utils/ids';
import { onSessionChange } from '../utils/session';

/**
 * `me` in one collection (GET /api/collections/{id}/me): the signed-in
 * account's public projection and their LINKED ITEM there — the item whose
 * account link (a `user` element with `options.identity`) names them — or
 * null when no card carries a link or no item is theirs.
 *
 * With `cardId`, the item is narrowed to that card's family (the card and
 * its descendants; `?card_id=`): "my record FOR THIS LOOKUP", which a
 * card-lookup's "Me" button and `options.defaultToMe` fill in. A Staff
 * lookup is never handed the caller's Resident record.
 *
 * One read per (collection, card) per session, shared by every surface that
 * asks (the "Me" chip's tooltip, the item form's "Me" shortcut, the
 * collection home's "Your record", every lookup of one card on a form);
 * `refresh()` re-reads every answer for the collection after the caller
 * links themselves. A failure (a collection the caller cannot meta-read, a
 * network blip) answers `{ user: null, item: null }` — every consumer treats
 * "no record" and "could not ask" the same way, by showing nothing.
 *
 * Returns `{ user, item, loading, refresh }`.
 */

const NONE = Object.freeze({ user: null, item: null });
// cacheKey -> Promise<{ user, item }>
const cache = new Map();
// Subscribers per cache key, so a refresh reaches every mounted consumer.
const listeners = new Map();

const keyOf = (collectionId, cardId) => (cardId ? `${collectionId}|${cardId}` : collectionId);

function load(collectionId, cardId, { force = false } = {}) {
	const key = keyOf(collectionId, cardId);
	if (!force && cache.has(key)) return cache.get(key);
	const promise = getCollectionMe(collectionId, { cardId })
		.then(data => Object.freeze({ user: data?.user || null, item: data?.item || null }))
		.catch(() => {
			cache.delete(key);
			return NONE;
		});
	cache.set(key, promise);
	return promise;
}

/**
 * The caller's record of a lookup card, as a promise — for a caller outside
 * React's render (the item form's `defaultToMe` pass), sharing the hook's
 * cache. Resolves `{ user, item }`; never rejects.
 */
export function loadMe(collectionId, { cardId = null } = {}) {
	const id = normalizeId(collectionId);
	if (!id) return Promise.resolve(NONE);
	return load(id, normalizeId(cardId) || null);
}

// `me` is the signed-in account's: another account (or none) forgets every
// answer, and a consumer still mounted (the shell outlives a sign-in) is
// re-asked — or told "nobody" when no one is signed in. A listener key is
// `collection` or `collection|card` (lookup "Me"); refreshMe takes the
// collection and re-reads every card-narrowed answer under it, so each
// collection is asked once — handing it the compound key would ask nothing.
onSessionChange(next => {
	cache.clear();
	if (next) {
		new Set([...listeners.keys()].map(key => key.split('|')[0])).forEach(refreshMe);
		return;
	}
	listeners.forEach(fns => fns.forEach(fn => fn(NONE)));
});

/** Test seam: forget every collection's answer. */
export function resetMeCache() {
	cache.clear();
}

/** Re-read `me` for a collection — every card-narrowed answer too — and tell every mounted consumer. */
export function refreshMe(collectionId) {
	const id = normalizeId(collectionId);
	if (!id) return Promise.resolve(NONE);
	const keys = new Set([id]);
	[...cache.keys(), ...listeners.keys()].forEach(key => { if (key.startsWith(`${id}|`)) keys.add(key); });
	const reads = [...keys].map(key => {
		const cardId = key.includes('|') ? key.slice(key.indexOf('|') + 1) : null;
		return load(id, cardId, { force: true }).then(answer => {
			(listeners.get(key) || new Set()).forEach(fn => fn(answer));
			return [key, answer];
		});
	});
	return Promise.all(reads).then(pairs => new Map(pairs).get(id) || NONE);
}

export default function useMe(collectionId, { enabled = true, cardId = null } = {}) {
	const id = enabled ? normalizeId(collectionId) : null;
	const card = id ? (normalizeId(cardId) || null) : null;
	const key = id ? keyOf(id, card) : null;
	const [state, setState] = useState({ key: null, answer: NONE, loading: !!key });

	useEffect(() => {
		if (!key) {
			setState({ key: null, answer: NONE, loading: false });
			return undefined;
		}
		let active = true;
		setState(prev => (prev.key === key ? prev : { key, answer: NONE, loading: true }));
		const onChange = answer => { if (active) setState({ key, answer, loading: false }); };
		if (!listeners.has(key)) listeners.set(key, new Set());
		listeners.get(key).add(onChange);
		load(id, card).then(onChange);
		return () => {
			active = false;
			listeners.get(key)?.delete(onChange);
		};
	}, [key, id, card]);

	const refresh = useCallback(() => (id ? refreshMe(id) : Promise.resolve(NONE)), [id]);

	const current = state.key === key ? state.answer : NONE;
	return {
		user: current.user,
		item: current.item,
		loading: !!key && (state.key !== key || state.loading),
		refresh,
	};
}
